/**
 * AI Prompts Settings Page
 * Allows users to configure AI personas for different features
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPromptTypes,
  getPersonasForType,
  setUserPreference,
  getUserPreferences,
  listPromptTemplates,
  type PromptType,
  type PersonaInfo,
  type UserPromptPreference,
} from '@services/api/promptTemplates';
import PromptEditorModal from '@components/settings/PromptEditorModal';

export default function AIPromptsPage() {
  const { t } = useTranslation();
  const [promptTypes, setPromptTypes] = useState<PromptType[]>([]);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [personas, setPersonas] = useState<PersonaInfo[]>([]);
  const [userPreference, setCurrentPreference] = useState<string | null>(null);
  const [userPreferences, setUserPreferences] = useState<UserPromptPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<{ type: string; persona: string; templateId?: string } | null>(null);

  // Load prompt types on mount
  useEffect(() => {
    const loadPromptTypes = async () => {
      try {
        const response = await getPromptTypes();
        setPromptTypes(response.types);
        if (response.types.length > 0) {
          setSelectedType(response.types[0]?.type ?? null);
        }
      } catch (err) {
        setError('Failed to load prompt types');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    void loadPromptTypes();
  }, []);

  // Load user preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await getUserPreferences();
        setUserPreferences(response.preferences);
      } catch (err) {
        console.error('Failed to load user preferences:', err);
      }
    };

    void loadPreferences();
  }, []);

  // Load personas when selected type changes
  useEffect(() => {
    if (!selectedType) return;

    const loadPersonas = async () => {
      try {
        const response = await getPersonasForType(selectedType);
        setPersonas(response.personas);
        setCurrentPreference(response.user_preference);
      } catch (err) {
        setError('Failed to load personas');
        console.error(err);
      }
    };

    void loadPersonas();
  }, [selectedType]);

  const handlePersonaSelect = async (personaName: string) => {
    if (!selectedType) return;

    setSaving(true);
    setError(null);

    try {
      await setUserPreference({
        prompt_type: selectedType,
        preferred_persona: personaName,
      });

      setCurrentPreference(personaName);

      // Refresh user preferences
      const response = await getUserPreferences();
      setUserPreferences(response.preferences);
    } catch (err) {
      setError('Failed to update preference');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getPreferenceForType = (type: string): string | undefined => {
    return userPreferences.find((p) => p.prompt_type === type)?.preferred_persona;
  };

  const handleViewEdit = async (personaName: string) => {
    if (!selectedType) return;

    try {
      // Try to find existing template for this persona
      const response = await listPromptTemplates(selectedType, true);
      const existingTemplate = response.templates.find(
        (t) => t.persona_name === personaName && t.prompt_type === selectedType
      );

      const editing: { type: string; persona: string; templateId?: string } = {
        type: selectedType,
        persona: personaName,
      };
      if (existingTemplate?.id) {
        editing.templateId = existingTemplate.id;
      }
      setEditingPersona(editing);
      setEditorOpen(true);
    } catch (err) {
      console.error('Failed to load template:', err);
      // Open editor anyway to create new
      const editing: { type: string; persona: string; templateId?: string } = {
        type: selectedType,
        persona: personaName,
      };
      setEditingPersona(editing);
      setEditorOpen(true);
    }
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingPersona(null);
  };

  const handleEditorSave = () => {
    // Refresh personas to show updated info
    if (selectedType) {
      const loadPersonas = async () => {
        try {
          const response = await getPersonasForType(selectedType);
          setPersonas(response.personas);
          setCurrentPreference(response.user_preference);
        } catch (err) {
          console.error('Failed to reload personas:', err);
        }
      };
      void loadPersonas();
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-neutral-500 dark:text-neutral-400">{t('common.loading')}</div>
      </div>
    );
  }

  const selectedPromptType = promptTypes.find((t) => t.type === selectedType);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
          {t('settings.aiPromptsSettings.title')}
        </h2>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          {t('settings.aiPromptsSettings.description')}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Prompt Types List */}
        <div className="lg:col-span-1">
          <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
            {t('settings.aiPromptsSettings.aiFeatures')}
          </h3>
          <div className="space-y-1">
            {promptTypes.map((type) => {
              const hasPreference = getPreferenceForType(type.type);
              return (
                <button
                  key={type.type}
                  onClick={() => setSelectedType(type.type)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                    selectedType === type.type
                      ? 'bg-primary-50 border-primary-200 text-primary-900 dark:bg-primary-900/20 dark:border-primary-800 dark:text-primary-100'
                      : 'bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50 dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-750'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{type.display_name}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">
                        {type.description}
                      </div>
                    </div>
                    {hasPreference && (
                      <span className="ml-2 flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        {t('settings.aiPromptsSettings.set')}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Column: Personas for Selected Type */}
        <div className="lg:col-span-2">
          {selectedPromptType && (
            <div>
              <div className="mb-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                      {selectedPromptType.display_name}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                      {selectedPromptType.description}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const personaName = prompt(t('settings.aiPromptsSettings.enterPersonaName'), 'custom-persona');
                      if (personaName && selectedType) {
                        const editing: { type: string; persona: string; templateId?: string } = {
                          type: selectedType,
                          persona: personaName,
                        };
                        setEditingPersona(editing);
                        setEditorOpen(true);
                      }
                    }}
                    className="ml-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium whitespace-nowrap"
                  >
                    + {t('settings.aiPromptsSettings.addPersona')}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {personas.map((persona) => {
                  const isSelected = userPreference === persona.name;
                  const isDefault = persona.name === 'professional' && !userPreference;

                  return (
                    <div
                      key={persona.name}
                      className={`relative border rounded-lg p-4 transition-all ${
                        isSelected || isDefault
                          ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/10'
                          : 'border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-neutral-900 dark:text-white">
                              {persona.display_name}
                            </h4>
                            {isDefault && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300">
                                {t('settings.aiPromptsSettings.default')}
                              </span>
                            )}
                            {isSelected && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                                {t('settings.aiPromptsSettings.active')}
                              </span>
                            )}
                            {persona.is_custom && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                                {t('settings.aiPromptsSettings.custom')}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                            {persona.description}
                          </p>
                        </div>
                        <div className="ml-4 flex gap-2">
                          <button
                            onClick={() => handleViewEdit(persona.name)}
                            className="px-3 py-2 rounded-lg text-sm font-medium border border-neutral-300 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-700"
                          >
                            {t('settings.aiPromptsSettings.viewEdit')}
                          </button>
                          <button
                            onClick={() => handlePersonaSelect(persona.name)}
                            disabled={saving || isSelected}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              isSelected
                                ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed dark:bg-neutral-700 dark:text-neutral-500'
                                : 'bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600'
                            }`}
                          >
                            {isSelected ? t('settings.aiPromptsSettings.selected') : t('settings.aiPromptsSettings.select')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {personas.length === 0 && selectedType && (
                <div className="text-center py-12">
                  <p className="text-neutral-500 dark:text-neutral-400 mb-4">
                    {t('settings.aiPromptsSettings.noPersonas')}
                  </p>
                  <button
                    onClick={() => {
                      const editing: { type: string; persona: string; templateId?: string } = {
                        type: selectedType,
                        persona: 'default',
                      };
                      setEditingPersona(editing);
                      setEditorOpen(true);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    {t('settings.aiPromptsSettings.createCustom')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">
              {t('settings.aiPromptsSettings.about')}
            </h4>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
              {t('settings.aiPromptsSettings.aboutDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Prompt Editor Modal */}
      {editingPersona && (
        <PromptEditorModal
          isOpen={editorOpen}
          onClose={handleEditorClose}
          promptType={editingPersona.type}
          personaName={editingPersona.persona}
          {...(editingPersona.templateId ? { templateId: editingPersona.templateId } : {})}
          onSave={handleEditorSave}
        />
      )}
    </div>
  );
}
